import type { PrayerDay } from "./types";

function parseHHMM(s: string): { hh: number; mm: number } | null {
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(String(s));
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return { hh, mm };
}

function isoToYMD(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map((x) => Number(x));
  return { y, m, d };
}

// Same technique used elsewhere in app: represent wall-clock time for the given date as a UTC epoch.
// This allows stable comparisons for "today in that timezone" without needing heavy TZ libraries.
function wallClockEpoch(dateISO: string, hhmm: string): number | null {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const { y, m, d } = isoToYMD(dateISO);
  return Date.UTC(y, m - 1, d, t.hh, t.mm, 0);
}

function partsInTimeZone(timeZone: string) {
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  const parts = dtf.formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")),
    minute: Number(get("minute")),
    second: Number(get("second"))
  };
}

export function sunProgress(day: PrayerDay, timeZone: string): number {
  // Approximation: sunrise -> maghrib (sunset-ish)
  const rise = wallClockEpoch(day.dateGregorianISO, day.timings.sunrise);
  const set = wallClockEpoch(day.dateGregorianISO, day.timings.maghrib);
  if (!rise || !set || set <= rise) return 0.5;

  const nowParts = partsInTimeZone(timeZone);
  const nowEpoch = Date.UTC(nowParts.year, nowParts.month - 1, nowParts.day, nowParts.hour, nowParts.minute, nowParts.second);

  const t = (nowEpoch - rise) / (set - rise);
  return Math.max(0, Math.min(1, t));
}

